import { useEffect, useState } from "react";
import { Info } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import api from "@/lib/api";

/**
 * Small (i) button + dialog that explains the classification rules for the
 * current view. `view` must be one of: "dashboard" | "new_customers" | "enterprise".
 * Fetches /api/rules lazily on first open, then caches in window for re-use.
 */
export default function RulesInfoDialog({ view, label }) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (window.__assureai_rules_cache) {
      setRules(window.__assureai_rules_cache[view]);
      return;
    }
    api.get("/rules").then((r) => {
      window.__assureai_rules_cache = r.data;
      setRules(r.data[view]);
    });
  }, [open, view]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 transition-colors"
        data-testid={`rules-info-btn-${view}`}
        title={`How ${label || "this view"} classifies firms`}
      >
        <Info size={14} weight="bold" />
        How is this classified?
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`rules-dialog-${view}`}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Info size={18} className="text-blue-600" />
              {rules?.title || "Classification rules"}
            </DialogTitle>
            {rules?.subtitle && (
              <div className="text-sm text-gray-600 mt-1">{rules.subtitle}</div>
            )}
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Priority list (Dashboard) */}
            {rules?.priority && (
              <ol className="space-y-2 list-none pl-0">
                {rules.priority.map((p, i) => (
                  <li key={`prio-${i}-${(p || "").slice(0, 24)}`} className="text-sm text-gray-700 leading-relaxed bg-blue-50/40 border border-blue-100 rounded-md px-3 py-2">
                    {p}
                  </li>
                ))}
              </ol>
            )}

            {/* Flat rows (Old Customers, Enterprise) */}
            {rules?.rows && <RulesTable rows={rules.rows} />}

            {/* Health Score formula (New Customers) */}
            {rules?.score_formula && (
              <div className="border border-blue-200 rounded-lg overflow-hidden">
                <div className="bg-blue-50 px-4 py-2.5 border-b border-blue-200">
                  <div className="font-display font-semibold text-sm text-gray-900">
                    {rules.score_formula.title}
                  </div>
                  {rules.score_formula.subtitle && (
                    <div className="text-xs text-gray-600 mt-0.5">{rules.score_formula.subtitle}</div>
                  )}
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {rules.score_formula.signals.map((s) => (
                      <tr key={s.label} className="border-b border-gray-100 last:border-b-0">
                        <td className="px-4 py-2.5 w-[210px] align-top">
                          <div className="text-xs font-semibold text-gray-900">{s.label}</div>
                          <div className="text-[10px] font-mono text-blue-700 mt-0.5">{s.weight}</div>
                        </td>
                        <td className="px-4 py-2.5 text-gray-700 text-xs leading-relaxed">{s.rule}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rules.score_formula.note && (
                  <div className="px-4 py-2.5 bg-blue-50/40 text-[11px] text-gray-600 italic border-t border-blue-100">
                    {rules.score_formula.note}
                  </div>
                )}
              </div>
            )}

            {/* Segmented rows (New Customers) */}
            {rules?.segments &&
              rules.segments.map((seg) => (
                <div key={seg.label} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                    <div className="font-display font-semibold text-sm text-gray-900">
                      {seg.label}
                    </div>
                    {seg.subtitle && (
                      <div className="text-xs text-gray-500 mt-0.5">{seg.subtitle}</div>
                    )}
                  </div>
                  <RulesTable rows={seg.rows} />
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RulesTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className="border-b border-gray-100 last:border-b-0">
            <td className="px-4 py-2.5 w-[170px] align-top">
              <span className="inline-flex items-center text-xs font-semibold text-gray-900 bg-gray-100 rounded-full px-2.5 py-1">
                {r.label}
              </span>
            </td>
            <td className="px-4 py-2.5 text-gray-700">{r.rule}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
